/** Refresh one view when its surface's hint moves, with a slow safety poll. */
import {
  subscribeInvalidation,
  type HintSurface,
} from './dashboard-invalidation'

/**
 * Runs `refresh` now, on every hint for `surfaces`, and as a fallback every
 * `healthyMs` while hints are live or every `unhealthyMs` while they are not
 * (the old fixed cadence, so a lost hint channel degrades to today's polling).
 * A hidden or offline tab does nothing and refreshes as soon as it returns.
 * Refreshes never overlap: a hint during a refresh runs once more after it.
 * While `urgent()` holds (e.g. the last read failed or found the resource
 * unavailable) the fast cadence applies even with live hints, because a
 * recovery may not be signalled.
 *
 * The returned stop function also carries `refresh`: an out-of-band refresh
 * (e.g. after a failed command) that goes through the same schedule, so its
 * outcome sets the next interval.
 */
export function hintedRefresh(
  tenant: string,
  surfaces: readonly HintSurface[],
  refresh: () => Promise<unknown> | void,
  {
    healthyMs,
    unhealthyMs,
    urgent = () => false,
  }: { healthyMs: number; unhealthyMs: number; urgent?: () => boolean }
): (() => void) & { refresh: () => Promise<void> } {
  let stopped = false
  let healthy = false
  let running = false
  let again = false
  let timer: ReturnType<typeof setTimeout> | undefined
  const active = () =>
    document.visibilityState !== 'hidden' && navigator.onLine !== false
  const schedule = () => {
    clearTimeout(timer)
    if (!stopped && active())
      timer = setTimeout(
        () => void run(),
        healthy && !urgent() ? healthyMs : unhealthyMs
      )
  }
  const run = async () => {
    clearTimeout(timer)
    if (stopped || !active()) return
    if (running) {
      again = true
      return
    }
    running = true
    try {
      await refresh()
    } catch {
      // The view reports its own failure; the schedule keeps going.
    } finally {
      running = false
      if (again && !stopped) {
        again = false
        void run()
      } else schedule()
    }
  }
  const resume = () => void run()
  document.addEventListener('visibilitychange', resume)
  window.addEventListener('online', resume)
  window.addEventListener('offline', resume)
  const stopHints = subscribeInvalidation(
    tenant,
    (live) => {
      healthy = live
      void run()
    },
    surfaces
  )
  // Joining an open hub reports its health at once, which already started
  // the first refresh; never read twice on mount.
  if (!running) void run()
  const stop = () => {
    stopped = true
    clearTimeout(timer)
    stopHints()
    document.removeEventListener('visibilitychange', resume)
    window.removeEventListener('online', resume)
    window.removeEventListener('offline', resume)
  }
  return Object.assign(stop, { refresh: run })
}
